import * as React from 'react';
import { pdfjs } from 'react-pdf';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../api';
import { Button, Size } from '../../../components/Button';
import { ImageUpload } from '../../../components/ImageUpload';
import { InfoMessage } from '../../../components/InfoMessage';
import * as utils from '../UploadDocumentPage/utils';
import * as logger from '../../../logger';

import defaultStyles from '../../../styles/index.module.css';
import formSetStyles from '../../../styles/formSetStyles.module.css';
import styles from './index.module.css';
import { EditTool } from './editTool';
import { ModalContainer } from '../../../components/ModalContainer';
import InfoLabel from '../../../components/ContextualHelpModal/InfoLabel';
import ContextualHelpModal from '../../../components/ContextualHelpModal';
import { Direction, Side } from '../../../utils/enum';

const pdfjsWorker = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

enum Labels {
  imageUploadFront = 'Front of',
  imageUploadBack = 'Back of',
}

export interface ProcessOcr {
  side: Side;
  sendToOcr: (patientUploadId: string) => void;
}

interface Props extends WithTranslation {
  navigateToNextPage: (isEmpty: boolean) => void;
  documentType: api.PatientUpload.DocumentType;
  label: string; // top page label
  labelReview: string; // top page label review document
  labelHasChanged: string; // no, my insurance/id has changed
  type: string; // type of card we're asking for
  continueLabel: string; // bottom page continue label
  encounter: api.Encounter.Encounter;
  required: boolean;
  onExistingInvalidated?: () => void;
  showEditTool?: boolean;
  processOcr?: ProcessOcr[];
  helpDialog?: string | null;
}

interface State {
  isLoading: boolean;
  document?: api.PatientUpload.PatientUpload;
  uploads: FileUpload[];
  isUploading: boolean;
  errorMsg?: string;
  uploadProgressClass: string | undefined;
  fileToEdit: {
    file: Blob;
    side: Side;
  } | null;
  showHelp: boolean;
}

interface FileUpload {
  dataUri: string;
  rawExtension: string;
  side: Side;
}

class UploadDocumentPage extends React.Component<Props, State> {
  formRef = React.createRef<HTMLFormElement>();
  // TODO remove when bug is fixed
  percentageLog = 0;
  percentage25timestamp = 0;
  percentage50timestamp = 0;
  percentage75timestamp = 0;
  percentage100timestamp = 0;
  totalBytesUploaded = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      uploads: [],
      isUploading: false,
      uploadProgressClass: undefined,
      fileToEdit: null,
      showHelp: false,
    };
  }

  async loadDocument() {
    let document: api.PatientUpload.PatientUpload | undefined;

    try {
      document = await api.client.PatientUpload.findOneByType(
        this.props.encounter.id,
        this.props.documentType,
      );
      if (document) {
        const { status, dataURL } =
          await api.client.PatientUpload.downloadImageFile(document.id);
        if (status === 200 && dataURL) {
          document.gsPath = dataURL;
        }
      }
    } catch (e) {}
    this.setState({
      document: document,
      isLoading: false,
    });
  }

  componentDidMount() {
    this.loadDocument();
  }

  onUploadProgressHandler = (event: api.AxiosProgressEvent): void => {
    if (event.event.lengthComputable) {
      this.updatePercentageLogs(event);
      let uploadClass = styles.Progress0;
      const percentage = Math.round((event.loaded / event.total!) * 100);

      if (percentage >= 96) {
        uploadClass = styles.Progress96;
      } else if (percentage >= 87) {
        uploadClass = styles.Progress87;
      } else if (percentage >= 72) {
        uploadClass = styles.Progress72;
      } else if (percentage >= 66) {
        uploadClass = styles.Progress66;
      } else if (percentage >= 52) {
        uploadClass = styles.Progress52;
      } else if (percentage >= 45) {
        uploadClass = styles.Progress45;
      } else if (percentage >= 33) {
        uploadClass = styles.Progress33;
      } else if (percentage >= 26) {
        uploadClass = styles.Progress26;
      } else if (percentage >= 19) {
        uploadClass = styles.Progress19;
      } else if (percentage >= 7) {
        uploadClass = styles.Progress7;
      }
      this.setState({
        uploadProgressClass: uploadClass,
      });
    }
  };

  mergePictures(front?: FileUpload, back?: FileUpload): Promise<string> {
    return utils.mergePictures({
      frontDataUri: front?.dataUri,
      backDataUri: back?.dataUri,
      direction: Direction.Vertical,
    });
  }

  dataURItoBlob(dataUri: string): Blob {
    var byteString = atob(dataUri.split(',')[1]);
    var mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0];
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  // TODO remove once upload bug is resolved
  updatePercentageLogs = (event: api.AxiosProgressEvent) => {
    this.percentageLog = Math.round((event.loaded / event.total!) * 100);
    this.totalBytesUploaded = event.loaded;
    const now = new Date().getTime();
    if (this.percentageLog >= 25 && !this.percentage25timestamp) {
      this.percentage25timestamp = now;
    }
    if (this.percentageLog >= 50 && !this.percentage50timestamp) {
      this.percentage50timestamp = now;
    }
    if (this.percentageLog >= 75 && !this.percentage75timestamp) {
      this.percentage75timestamp = now;
    }
  };

  async processUpload(mergedBlob: Blob, url: string): Promise<number> {
    let status = 0;
    this.percentageLog = 0;
    this.percentage25timestamp = 0;
    this.percentage50timestamp = 0;
    this.percentage75timestamp = 0;
    this.percentage100timestamp = 0;
    this.totalBytesUploaded = 0;
    try {
      status = await api.client.PatientUpload.upload(
        mergedBlob,
        url,
        this.onUploadProgressHandler,
      );
    } catch (e) {
      this.setState({
        errorMsg: this.props.t('failedUploadInternetError'),
      });
      logger.error(
        e,
        `[UploadDocumentPage][upload] timeout size: ${mergedBlob.size} bytes, totalSent=${this.totalBytesUploaded}, percentage=${this.percentageLog}%` +
          `, 25%=${this.percentage25timestamp}, 50%=${this.percentage50timestamp}, 75%=${this.percentage75timestamp}`,
      );
    }
    return status;
  }

  maybeUploadFileToOcr = async (blob: Blob, side: Side): Promise<void> => {
    if (!this.props.processOcr) return;
    const sideToProcess = this.props.processOcr.find(
      (process) => process.side === side,
    );
    if (!sideToProcess) return;
    try {
      const { id, url } = await api.client.PatientUpload.getSignedUploadURL(
        this.props.encounter.id,
        api.PatientUpload.DocumentType.OcrVerification,
      );
      if (url) {
        const status = await api.client.PatientUpload.upload(
          blob,
          url,
          () => {},
        );
        if (status === 200) {
          await api.client.PatientUpload.validateUploadedDocuments(id);
          sideToProcess.sendToOcr(id);
        }
      }
    } catch (e) {
      logger.error(
        e,
        `[UploadDocumentPage][maybeUploadFileToOcr] size: ${blob.size} bytes, error: ${e}`,
      );
    }
  };

  upload = async (): Promise<void> => {
    const { t } = this.props;
    if (this.state.uploads.length === 2) {
      this.setState({
        errorMsg: undefined,
        isUploading: true,
        uploadProgressClass: styles.Progress0,
      });
      try {
        const merged = await this.mergePictures(
          this.state.uploads.find((upload) => upload.side === Side.Front),
          this.state.uploads.find((upload) => upload.side === Side.Back),
        );

        const { id, url } = await api.client.PatientUpload.getSignedUploadURL(
          this.props.encounter.id,
          this.props.documentType,
        );
        if (url) {
          const mergedBlob = this.dataURItoBlob(merged);
          const status = await this.processUpload(mergedBlob, url);
          if (status === 200) {
            await api.client.PatientUpload.validateUploadedDocuments(id);
            this.props.navigateToNextPage(false);
          }
        }
      } catch (e) {
        this.setState({
          errorMsg: t('errorOccuredTryAgain'),
        });
        logger.error(e, `[UploadDocumentPage][upload]`);
      }
    }
    this.setState({
      isUploading: false,
      uploadProgressClass: undefined,
    });
  };

  isSubmitDisabled(): boolean {
    if (this.state.isUploading || this.state.uploads.length === 1) {
      return true;
    }
    return false;
  }

  cancelUpload = (): void => {
    if (this.formRef.current) {
      this.formRef.current.reset();
    }
    this.setState({ uploads: [], errorMsg: '' });
  };

  renderSubmitButton() {
    const isEmpty: boolean = !this.state.uploads.length;
    if (isEmpty) {
      const parseDocumentType = {
        [api.PatientUpload.DocumentType.DocumentSigner]: 'documents',
        [api.PatientUpload.DocumentType.InsuranceCard]: 'Insurance Card',
        [api.PatientUpload.DocumentType.PhotoID]: 'Photo ID',
        [api.PatientUpload.DocumentType.OcrVerification]: '',
        [api.PatientUpload.DocumentType.FreehandBodyDiagram]: 'Body Diagram',
        [api.PatientUpload.DocumentType.FormSummary]: 'Form Summary',
      };

      const handleContinue = () => {
        if (this.props.required) {
          this.setState({
            errorMsg: `Upload your ${
              parseDocumentType[this.props.documentType]
            } to continue.`,
          });
          window.scrollTo(0, 0);
          return;
        }

        this.props.navigateToNextPage(true);
      };

      return (
        <div className={formSetStyles.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={this.props.t('Continue')}
            onClick={handleContinue}
            invertColors={false}
          />
        </div>
      );
    }
    return (
      <div className={formSetStyles.ButtonSubmitWrapper}>
        <Button
          type="button"
          children={
            this.state.uploadProgressClass ? (
              <span className={styles.Spinner}></span>
            ) : (
              this.props.t('Upload')
            )
          }
          className={
            this.state.uploadProgressClass
              ? `${styles.Uploading} ${this.state.uploadProgressClass}`
              : ''
          }
          onClick={this.upload}
          invertColors={isEmpty}
          disabled={this.isSubmitDisabled()}
        />
        <Button
          type="button"
          children={this.props.t('Clear images')}
          disabled={this.state.isUploading}
          invertColors={true}
          onClick={this.cancelUpload}
        />
      </div>
    );
  }

  async getDataUriFromFile(file: Blob): Promise<string> {
    return new Promise<string>((resolve) => {
      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result.toString());
        }
      };
    });
  }

  fileChanged = async (file: Blob, side: Side, isEdited?: boolean) => {
    const { t } = this.props;
    if (!isEdited && this.props.showEditTool) {
      if (file.type === 'application/pdf') {
        try {
          const pdfToImage = await utils.convertPdfToImage(file);
          this.setState({ fileToEdit: { file: pdfToImage, side } });
        } catch (e) {
          this.setState({
            errorMsg: t('errorOccuredRetryImage'),
          });
          logger.error(
            e,
            `[UploadDocumentPage][fileChanged] failed to convert PDF to image`,
          );
        }
      } else {
        this.setState({ fileToEdit: { file, side } });
      }
    } else {
      const dataUri = await this.getDataUriFromFile(file);
      const uploads = this.state.uploads.filter((upload) => {
        return upload && upload.side !== side;
      });
      uploads.push({
        dataUri: dataUri,
        rawExtension: file.type,
        side: side,
      });
      this.setState((state) => ({
        ...state,
        uploads,
        errorMsg: undefined,
        fileToEdit: null,
      }));
    }
  };

  isReviewing(): boolean {
    if (
      this.state.document &&
      this.state.document.status === api.PatientUpload.UploadStatus.Verified
    ) {
      return true;
    }
    return false;
  }

  getPreview(side: Side): string | undefined {
    const upload = this.state.uploads.find((upload) => upload.side === side);
    if (upload) {
      return upload.dataUri;
    }
  }

  renderUpload() {
    const { uploads, fileToEdit } = this.state;
    const secureMessage = this.props.t('dataIsSecure');
    if (fileToEdit) {
      return (
        <ModalContainer
          isOpen={true}
          noPadding={true}
          isDarkMode={true}
          onModalClose={() => {
            this.setState({ fileToEdit: null });
          }}
          contentClassName={styles.MaxWidth}
          component={
            <EditTool
              onSave={(picture: Blob) => {
                this.maybeUploadFileToOcr(picture, fileToEdit.side);
                this.fileChanged(picture, fileToEdit.side, true);
              }}
              file={fileToEdit.file}
            />
          }
        />
      );
    }

    return (
      <>
        <div className={styles.Title}>{this.props.label}</div>
        {!uploads.length && (
          <div className={styles.SecureMessage}>
            <span className={styles.Lock}></span>
            <p>{secureMessage}</p>
          </div>
        )}
        <ImageUpload
          label={this.props.t(`${Labels.imageUploadFront} ${this.props.type}`)}
          side={Side.Front}
          fileChanged={this.fileChanged}
          preview={this.getPreview(Side.Front)}
        />
        <ImageUpload
          label={this.props.t(`${Labels.imageUploadBack} ${this.props.type}`)}
          side={Side.Back}
          fileChanged={this.fileChanged}
          preview={this.getPreview(Side.Back)}
        />
        {this.renderHelpDialog()}
        {this.renderSubmitButton()}
      </>
    );
  }

  resetUpload = async () => {
    if (this.state.document) {
      try {
        await api.client.PatientUpload.invalidateCurrentDocument(
          this.state.document.id,
        );
      } catch (e) {
        logger.error(
          e,
          `[UploadDocumentPage.resetUpload] could not invalidate current document`,
        );
        this.setState({ errorMsg: this.props.t('errorOccuredTryAgain') });
        return;
      }
    }
    this.setState({
      document: undefined,
    });
    if (this.props.onExistingInvalidated) {
      this.props.onExistingInvalidated();
    }
  };

  renderReview() {
    return (
      <>
        <div className={styles.Title}>{this.props.labelReview}</div>
        <div className={styles.DocumentReview}>
          <img src={this.state.document?.gsPath} alt="review" />
        </div>
        {this.renderHelpDialog()}
        <div className={formSetStyles.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={this.props.t('Yes')}
            onClick={() => this.props.navigateToNextPage(false)}
          />
          <Button
            type="button"
            children={this.props.labelHasChanged}
            invertColors={true}
            onClick={this.resetUpload}
            size={Size.SmallHorizontalPadding}
          />
        </div>
      </>
    );
  }

  renderHelpDialog() {
    const { helpDialog, t } = this.props;
    if (!helpDialog) return null;
    return (
      <>
        <ContextualHelpModal
          headerText={t('More information')}
          helpText={helpDialog}
          isOpen={this.state.showHelp}
          onRequestClose={() => this.setState({ showHelp: false })}
          onHandleHelpful={async () => this.setState({ showHelp: false })}
        />
        <InfoLabel
          className={styles.InfoLabel}
          onClick={() => this.setState({ showHelp: true })}
          label={t('Need help?')}
        />
      </>
    );
  }

  render() {
    if (this.state.isLoading) {
      return <div className={defaultStyles.Loading}>Loading...</div>;
    }
    return (
      <form className={`${styles.CardUploadPage}`} ref={this.formRef}>
        {this.state.errorMsg ? (
          <InfoMessage isError={true}>{this.state.errorMsg}</InfoMessage>
        ) : null}
        {this.isReviewing() ? this.renderReview() : this.renderUpload()}
      </form>
    );
  }
}

export default withTranslation()(UploadDocumentPage);
